import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Stack } from '@mui/material';
import { useIntl } from 'react-intl';

import { ConceptGroupMetaStrip, type MetaStripItem } from './ConceptGroupMetaStrip';
import { ConceptsExplorer, ConceptsSearchField, type ConceptsTableModel } from './ConceptsExplorer';
import { SkippedEntriesNotice } from './SkippedEntriesNotice';

/**
 * The concepts region shared by the Draft review's Concepts tab and the Ready
 * detail view: a `Concepts: N` strip with the search field + CSV download pinned
 * right, then the flat concepts grid. Both surfaces feed it a
 * {@link ConceptsTableModel} (preview vs created source) so they look + operate
 * identically — the search box, download, paging and grid are one implementation.
 *
 * Testids mirror the original draft layout: the strip is namespaced
 * ``${testIdPrefix}-concepts`` while the search + grid use ``testIdPrefix``.
 */
export const ConceptGroupConceptsPanel = ({
  model,
  testIdPrefix,
  conceptCount,
  invalidValues = [],
  canDownload = true,
}: {
  model: ConceptsTableModel;
  testIdPrefix: string;
  conceptCount: number | null;
  /** Direct-upload skipped entries (Draft only); omitted on the Ready view. */
  invalidValues?: string[];
  canDownload?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const metaItems: MetaStripItem[] =
    conceptCount != null
      ? [
          {
            key: 'concepts',
            icon: <CheckCircleIcon fontSize="small" color="success" />,
            label: formatMessage({ id: 'epiChat.conceptGroupPreview.meta.conceptsLabel' }),
            value: conceptCount,
          },
        ]
      : [];

  // paddingBottom keeps the grid's pagination footer off the panel's bottom edge
  // (the Draft body Box has no bottom padding of its own).
  return (
    <Stack sx={{ gap: 1.5, height: '100%', minHeight: 0, paddingBottom: 6 }}>
      <SkippedEntriesNotice values={invalidValues} />
      <ConceptGroupMetaStrip
        items={metaItems}
        onDownload={canDownload ? model.handleDownload : undefined}
        testIdPrefix={`${testIdPrefix}-concepts`}
        // Search sits on the strip, next to the download (design feedback).
        endContent={<ConceptsSearchField model={model} testIdPrefix={testIdPrefix} />}
      />
      {/* Fill the remaining height; the explorer's own search + download are
          suppressed (they live on the strip above). */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ConceptsExplorer
          model={model}
          testIdPrefix={testIdPrefix}
          canDownload={canDownload}
          showDownload={false}
          showSearch={false}
        />
      </Box>
    </Stack>
  );
};
